/**
 * 내 계정
 * ---------------------------------------------------------------------------
 * 비밀번호 변경, 2차 인증(TOTP) 등록, 활성 세션 관리.
 *
 * 비밀번호를 바꾸면 서버가 현재 세션만 남기고 나머지를 모두 종료한다.
 * 신뢰 기기도 함께 해제된다. 사용자가 놀라지 않도록 미리 알려 준다.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  List,
  Modal,
  Space,
  Tag,
  Typography,
} from 'antd';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText, qk } from '../../lib/queries';

const { Text, Paragraph } = Typography;

interface MyProfile {
  userId: string;
  name: string;
  email: string;
  phone: string | null;
  birthDate: string | null;
  companyName: string;
  departmentName: string | null;
  empNo: string | null;
  mfaEnrolled: { email: boolean; totp: boolean; backupCodesRemaining: number };
}

interface SessionRow {
  sessionId: string;
  deviceLabel: string | null;
  clientIp: string | null;
  createdAt: string;
  lastSeenAt: string;
  current: boolean;
}

export default function AccountPage() {
  const { message } = AntdApp.useApp();
  const client = useQueryClient();
  const [pwForm] = Form.useForm();

  const [changing, setChanging] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [totp, setTotp] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  /*
   * 연락처는 본인이 고친다.
   *
   * `PATCH /auth/me` 는 처음부터 있었지만 **이 화면에는 부르는 곳이 없었다** —
   * 제품(WORK&CRM)의 '내 정보'만 쓰고 있었다. 그래서 Hub 에서는 자기 번호를
   * 바꾸려면 인사담당자에게 부탁해야 했다.
   */
  const [editingPhone, setEditingPhone] = useState(false);
  const [phone, setPhone] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);

  const savePhone = async () => {
    setSavingPhone(true);
    try {
      // 빈 칸은 "지우라" 는 뜻이다. 빈 문자열을 보내면 형식 검사에 걸린다.
      await apiClient.patch('/auth/me', { phone: phone.trim() === '' ? null : phone.trim() });
      message.success('연락처를 변경했습니다.');
      setEditingPhone(false);
      await client.invalidateQueries({ queryKey: qk.me });
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    } finally {
      setSavingPhone(false);
    }
  };

  const me = useQuery<MyProfile, NormalizedError>({
    queryKey: qk.me,
    queryFn: async () => (await apiClient.get<MyProfile>('/auth/me')).data,
  });

  const sessions = useQuery<SessionRow[], NormalizedError>({
    queryKey: qk.sessions,
    queryFn: async () => (await apiClient.get<SessionRow[]>('/auth/sessions')).data,
  });

  const changePassword = async () => {
    const values = await pwForm.validateFields().catch(() => null);
    if (!values) return;

    setChanging(true);
    setPwError(null);
    try {
      await apiClient.post('/auth/password/change', values);
      message.success('비밀번호를 변경했습니다. 다른 기기의 세션은 모두 종료되었습니다.');
      pwForm.resetFields();
      await client.invalidateQueries({ queryKey: qk.sessions });
    } catch (err) {
      setPwError(errorText(err as NormalizedError));
    } finally {
      setChanging(false);
    }
  };

  const enrollTotp = async () => {
    try {
      const { data } = await apiClient.post<{ secret: string; otpauthUrl: string }>(
        '/auth/mfa/totp/enroll',
      );
      setTotp(data);
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    }
  };

  const confirmTotp = async (code: string) => {
    try {
      const { data } = await apiClient.post<{ backupCodes: string[] }>('/auth/mfa/totp/confirm', {
        code,
      });
      setTotp(null);
      setBackupCodes(data.backupCodes);
      await client.invalidateQueries({ queryKey: qk.me });
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    }
  };

  const revoke = async (sessionId: string) => {
    try {
      await apiClient.delete(`/auth/sessions/${sessionId}`);
      message.success('세션을 종료했습니다.');
      await client.invalidateQueries({ queryKey: qk.sessions });
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 780 }}>
      <Card
        title="내 정보"
        loading={me.isLoading}
        extra={
          editingPhone ? null : (
            <Button size="small" onClick={() => { setPhone(me.data?.phone ?? ''); setEditingPhone(true); }}>
              연락처 변경
            </Button>
          )
        }
      >
        <Descriptions column={2} size="small">
          <Descriptions.Item label="이름">{me.data?.name}</Descriptions.Item>
          <Descriptions.Item label="이메일">{me.data?.email}</Descriptions.Item>
          <Descriptions.Item label="기업">{me.data?.companyName}</Descriptions.Item>
          <Descriptions.Item label="부서">
            {me.data?.departmentName ?? <Text type="secondary">미지정</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="사번">
            {me.data?.empNo ?? <Text type="secondary">미지정</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="연락처">
            {editingPhone ? (
              <Space.Compact>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="010-0000-0000"
                  style={{ width: 160 }}
                  onPressEnter={savePhone}
                />
                <Button type="primary" loading={savingPhone} onClick={savePhone}>
                  저장
                </Button>
                <Button onClick={() => setEditingPhone(false)}>취소</Button>
              </Space.Compact>
            ) : (
              me.data?.phone ?? <Text type="secondary">미등록</Text>
            )}
          </Descriptions.Item>
        </Descriptions>
        <Text type="secondary" style={{ fontSize: 12 }}>
          이름·부서·사번은 인사 기록이라 인사담당자가 정합니다. 연락처만 본인이 바꿀 수 있습니다.
        </Text>
      </Card>

      <Card title="비밀번호 변경">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {pwError && <Alert type="error" showIcon message={pwError} />}
          <Alert
            type="warning"
            showIcon
            message="변경하면 다른 기기의 로그인이 모두 종료됩니다"
            description="현재 창만 유지되고, 등록된 신뢰 기기도 함께 해제됩니다."
          />
          <Form form={pwForm} layout="vertical" requiredMark={false}>
            <Form.Item
              name="currentPassword"
              label="현재 비밀번호"
              rules={[{ required: true, message: '현재 비밀번호를 입력하세요' }]}
            >
              <Input.Password autoComplete="current-password" />
            </Form.Item>
            <Form.Item
              name="newPassword"
              label="새 비밀번호"
              rules={[
                { required: true, message: '새 비밀번호를 입력하세요' },
                { min: 12, message: '12자 이상이어야 합니다' },
              ]}
              extra="12자 이상, 영문·숫자 포함. 외부 유출 이력이 있는 비밀번호는 사용할 수 없습니다."
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              label="새 비밀번호 확인"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: '새 비밀번호를 다시 입력하세요' },
                ({ getFieldValue }) => ({
                  validator: (_, value) =>
                    !value || getFieldValue('newPassword') === value
                      ? Promise.resolve()
                      : Promise.reject(new Error('비밀번호가 일치하지 않습니다')),
                }),
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Button type="primary" onClick={changePassword} loading={changing}>
              비밀번호 변경
            </Button>
          </Form>
        </Space>
      </Card>

      <Card
        title="2차 인증 (인증 앱)"
        extra={
          me.data?.mfaEnrolled.totp ? (
            <Tag color="green">등록됨</Tag>
          ) : (
            <Tag color="orange">미등록</Tag>
          )
        }
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            이메일 인증코드는 이메일 계정이 뚫리면 함께 뚫립니다. 인증 앱(TOTP)이 더 안전합니다.
            {me.data?.mfaEnrolled.totp &&
              ` 남은 백업 코드 ${me.data.mfaEnrolled.backupCodesRemaining}개.`}
          </Paragraph>
          <Button onClick={enrollTotp}>
            {me.data?.mfaEnrolled.totp ? '인증 앱 다시 등록' : '인증 앱 등록'}
          </Button>
        </Space>
      </Card>

      <Card title="활성 세션" loading={sessions.isLoading}>
        {/*
          동시 접속을 1대로 제한한 기업에서는 여기에 늘 한 줄만 보인다.
          그 경우 「종료」 단추가 아예 나오지 않는데, 설명이 없으면 기능이
          고장 난 것처럼 보인다.
        */}
        <Text type="secondary" style={{ fontSize: 12 }}>
          회사의 보안 정책이 동시 접속을 1대로 제한하면, 다른 기기에서 로그인하는 순간 이전
          기기는 자동으로 끊기고 여기에는 한 줄만 보입니다.
        </Text>
        <List
          dataSource={sessions.data ?? []}
          locale={{ emptyText: '활성 세션이 없습니다.' }}
          renderItem={(s) => (
            <List.Item
              actions={
                s.current
                  ? [<Tag key="cur" color="blue">현재 세션</Tag>]
                  : [
                      <Button key="revoke" size="small" danger onClick={() => revoke(s.sessionId)}>
                        종료
                      </Button>,
                    ]
              }
            >
              <List.Item.Meta
                title={s.deviceLabel ?? '알 수 없는 기기'}
                description={
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    IP {s.clientIp ?? '-'} · 최근 활동{' '}
                    {new Date(s.lastSeenAt).toLocaleString('ko-KR')}
                  </Text>
                }
              />
            </List.Item>
          )}
        />
      </Card>

      {/* TOTP 등록 */}
      <Modal
        open={Boolean(totp)}
        title="인증 앱 등록"
        onCancel={() => setTotp(null)}
        footer={null}
        destroyOnHidden
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Paragraph style={{ marginBottom: 0 }}>
            Google Authenticator 등 인증 앱에 아래 키를 등록한 뒤, 앱에 표시된 6자리 코드를
            입력하세요.
          </Paragraph>
          <Input.TextArea readOnly value={totp?.secret} autoSize />
          <Text type="secondary" style={{ fontSize: 12, wordBreak: 'break-all' }}>
            {totp?.otpauthUrl}
          </Text>
          <Form onFinish={(v: { code: string }) => confirmTotp(v.code)} layout="inline">
            <Form.Item
              name="code"
              rules={[{ required: true, pattern: /^\d{6}$/, message: '6자리 숫자' }]}
            >
              <Input placeholder="000000" maxLength={6} style={{ width: 140 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit">
              등록 완료
            </Button>
          </Form>
        </Space>
      </Modal>

      {/* 백업 코드 */}
      <Modal
        open={Boolean(backupCodes)}
        title="백업 코드"
        onCancel={() => setBackupCodes(null)}
        onOk={() => setBackupCodes(null)}
        okText="저장했습니다"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="지금만 볼 수 있습니다"
            description="인증 앱을 분실했을 때 사용합니다. 안전한 곳에 보관하세요. 각 코드는 1회만 사용할 수 있습니다."
          />
          <Input.TextArea readOnly rows={5} value={(backupCodes ?? []).join('\n')} />
        </Space>
      </Modal>
    </Space>
  );
}
